import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { ErrorBoundary, reactPlugin } from "@datadog/browser-rum-react";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { ThemeProvider } from "@mui/material";
import { composeWrappers } from "@redotech/react-util/component";
import { HidProvider } from "@redotech/react-util/hid";
import { RedoClient } from "@redotech/redo-api-client";
import { CampaignProvider } from "@redotech/redo-marketplace-app-common/campaign-context";
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  REDO_API_URL,
  REDO_MARKETPLACE_APP_VERSION,
  REDO_MARKETPLACE_SERVER_URL,
} from "@redotech/redo-marketplace-app-common/config";
import { RedoMarketplaceRpcClientProvider } from "@redotech/redo-marketplace-app-common/rpc-client";
import {
  VendorAuthContext,
  VendorAuthProvider,
} from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoClientContext } from "@redotech/redo-web/client";
import { MUI_THEME } from "@redotech/redo-web/mui-theme";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { ThemeProvider as RedoThemeProvider } from "@redotech/redo-web/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { CartProvider } from "./context/cart-provider";
import { CartUIProvider } from "./context/cart-ui-provider";
import { routes } from "./route";
import "./styles.css";

if (
  process.env.NODE_ENV !== "development" &&
  DATADOG_CLIENT_TOKEN &&
  DATADOG_APPLICATION_ID
) {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: "marketplace-app",
    env: process.env.NODE_ENV || "production",
    version: REDO_MARKETPLACE_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 1,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    plugins: [reactPlugin({ router: true })],
  });
  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: "datadoghq.com",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: "marketplace-app",
    version: REDO_MARKETPLACE_APP_VERSION,
  });
}

const router = createBrowserRouter(routes);

const ErrorFallback = ({
  resetError,
  error,
}: {
  resetError: () => void;
  error: Error;
}) => (
  <div>
    <h2>Something went wrong</h2>
    <p>{String(error)}</p>
    <button onClick={resetError}>Try again</button>
  </div>
);

const MarketplaceApp = () => {
  return composeWrappers(
    (inner) => <ErrorBoundary fallback={ErrorFallback}>{inner}</ErrorBoundary>,
    (inner) => (
      <QueryClientProvider client={reactQueryClient}>
        {inner}
        {process.env.NODE_ENV === "development" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    ),
    (inner) => <HidProvider>{inner}</HidProvider>,
    (inner) => <ThemeProvider theme={MUI_THEME}>{inner}</ThemeProvider>,
    (inner) => (
      <IntercomProvider
        appId={globalThis.INTERCOM_APP_ID || ""}
        autoBoot
        autoBootProps={{ hideDefaultLauncher: true }}
        shouldInitialize={false}
      >
        {inner}
      </IntercomProvider>
    ),
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
    (inner) => <VendorAuthProvider>{inner}</VendorAuthProvider>,
    (inner) => (
      <VendorAuthContext.Consumer>
        {(auth) => (
          <RedoClientContext.Provider
            value={new RedoClient(REDO_API_URL, auth?.token)}
          >
            {inner}
          </RedoClientContext.Provider>
        )}
      </VendorAuthContext.Consumer>
    ),
    (inner) => (
      <VendorAuthContext.Consumer>
        {(auth) => (
          <RedoMarketplaceRpcClientProvider
            authToken={auth?.token ? `Bearer ${auth.token}` : undefined}
            baseUrl={REDO_MARKETPLACE_SERVER_URL}
          >
            {inner}
          </RedoMarketplaceRpcClientProvider>
        )}
      </VendorAuthContext.Consumer>
    ),
    (inner) => <RedoThemeProvider>{inner}</RedoThemeProvider>,
    (inner) => <CampaignProvider>{inner}</CampaignProvider>,
    (inner) => <CartUIProvider>{inner}</CartUIProvider>,
    (inner) => <CartProvider>{inner}</CartProvider>,
  )(<RouterProvider router={router} />);
};

const root = createRoot(document.getElementById("root")!);
root.render(<MarketplaceApp />);
